// Validation Connector
// ====================
//
// Validation connector between .NET and JustValidate.
// It parses the HTML data- attributes generated by .NET,
// and passes the logic to JustValidate.

import JustValidate from 'just-validate';
import JustValidatePluginDate from 'just-validate-plugin-date';
import JustValidatePluginRequiredIf from './JustValidatePluginRequiredIf';

const DEFAULT_CONFIG = {
    errorFieldCssClass: '',
    errorFieldStyle: '',
    errorLabelCssClass: 'invalid-feedback',
    errorLabelStyle: '',
    successFieldCssClass: '',
    successFieldStyle: '',
    successLabelCssClass: 'valid-feedback',
    successLabelStyle: '',
    lockForm: false,
};

class ValidationConnector {
    constructor(form, config = {}) {
        this.form = form;
        this.validation = new JustValidate(form, { ...DEFAULT_CONFIG, ...config });
        this.init();
    }

    init() {
        this.form.querySelectorAll('[data-val=true]').forEach((field) => {
            const rules = [];

            //
            // Required
            //

            // .NET RequiredAttribute
            if (field.dataset.valRequired) {
                if (field.tagName === 'FIELDSET') {
                    this.validation.addRequiredGroup(field, field.dataset.valRequired, { errorLabelStyle: { display: 'block' } });
                } else {
                    rules.push({
                        rule: 'required',
                        errorMessage: field.dataset.valRequired,
                    });
                }
            }

            //
            // Required if a field value is equal the given value
            //

            // .NET RequiredIfAttribute
            if (field.dataset.valRequiredif) {
                const requiredIfPlugin = new JustValidatePluginRequiredIf(this.validation, field);
                rules.push({
                    plugin: (value, fields) => requiredIfPlugin.validate(value, fields),
                    errorMessage: field.dataset.valRequiredif,
                });
            }

            //
            // Max length
            //

            // .NET StringLengthAttribute
            if (field.dataset.valLengthMax) {
                rules.push({
                    rule: 'maxLength',
                    value: +field.dataset.valLengthMax,
                    errorMessage: field.dataset.valLength,
                });
            }

            // .NET MinLengthAttribute
            if (field.dataset.valMaxlengthMax) {
                rules.push({
                    rule: 'maxLength',
                    value: +field.dataset.valMaxlengthMax,
                    errorMessage: field.dataset.valMaxlength,
                });
            }

            //
            // Max number
            //

            // .NET RangeAttribute
            if (field.dataset.valRangeMax) {
                rules.push({
                    rule: 'maxNumber',
                    value: +field.dataset.valRangeMax,
                    errorMessage: field.dataset.valRange,
                });
            }

            //
            // Min length
            //

            // .NET StringLengthAttribute
            if (field.dataset.valLengthMin) {
                rules.push({
                    rule: 'minLength',
                    value: +field.dataset.valLengthMin,
                    errorMessage: field.dataset.valLength,
                });
            }

            // .NET MaxLengthAttribute
            if (field.dataset.valMinlengthMin) {
                rules.push({
                    rule: 'minLength',
                    value: +field.dataset.valMinlengthMin,
                    errorMessage: field.dataset.valMinlength,
                });
            }

            //
            // Min number
            //

            // .NET RangeAttribute
            if (field.dataset.valRangeMin) {
                rules.push({
                    rule: 'minNumber',
                    value: +field.dataset.valRangeMin,
                    errorMessage: field.dataset.valRange,
                });
            }

            //
            // Regular expression
            //

            // .NET RegularExpressionAttribute
            if (field.dataset.valRegexPattern) {
                rules.push({
                    rule: 'customRegexp',
                    value: field.dataset.valRegexPattern,
                    errorMessage: field.dataset.valRegex,
                });
            }

            //
            // Date
            //

            if (field.dataset.valDateFormat) {
                rules.push({
                    plugin: JustValidatePluginDate(() => ({
                        format: field.dataset.valDateFormat,
                    })),
                    errorMessage: field.dataset.valDate,
                });
            }

            //
            // Email
            //

            // .NET EmailAddressAttribute
            if (field.dataset.valEmail) {
                rules.push({
                    rule: 'email',
                    errorMessage: field.dataset.valEmail,
                });
            }

            //
            // Equal to
            //

            // .NET EqualsAttribute
            if (field.dataset.valEqualtoOther) {
                const to = this.form[field.dataset.valEqualtoOther.match(/\w*$/)[0]];
                to.addEventListener('input', (e) => {
                    if (this.validation.isSubmitted) {
                        this.validation.revalidateField(`#${field.id}`);
                    }
                });
                rules.push({
                    validator: (value) => to.value === value,
                    errorMessage: field.dataset.valEqualto,
                });
            }

            if (rules.length > 0) this.validation.addField(`#${field.id}`, rules, { errorFieldCssClass: 'is-invalid' });
        });
    }
}

export default ValidationConnector;
